import {
  type ClearIndicatorProps,
  type DropdownIndicatorProps,
  type GroupBase,
  type MultiValueGenericProps,
  type MultiValueRemoveProps,
  components,
} from 'react-select';
import { Children } from 'react';
import { ChevronDown, X, XIcon } from 'lucide-react';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Add spellcheck property to the input element
export const CustomInput = (props: any) => <components.Input {...props} spellCheck={true} />;

export const DropdownIndicator = <TOption, IsMulti extends boolean, Group extends GroupBase<TOption>>(
  props: DropdownIndicatorProps<TOption, IsMulti, Group>,
) => (
  <components.DropdownIndicator {...props}>
    <ChevronDown className="size-6" strokeWidth={2} />
  </components.DropdownIndicator>
);

export const ClearIndicator = <TOption, IsMulti extends boolean, Group extends GroupBase<TOption>>(
  props: ClearIndicatorProps<TOption, IsMulti, Group>,
) => (
  <components.ClearIndicator {...props}>
    <X className="size-6 max-h-6 min-h-6 max-w-6 min-w-6" strokeWidth={2} />
  </components.ClearIndicator>
);

export const CustomMultiValueRemove = <TOption, IsMulti extends boolean, Group extends GroupBase<TOption>>(
  props: MultiValueRemoveProps<TOption, IsMulti, Group> & { isIndeterminate?: boolean },
) => (
  <components.MultiValueRemove {...props}>
    <XIcon className={cn('icon-3', props.isIndeterminate && 'stroke-status-warning-dark')} strokeWidth={3} />
  </components.MultiValueRemove>
);

// react-select types `MultiValueGenericProps.data` as `any` regardless of the `Option` generic
// (an upstream quirk, not something a generic parameter here can fix), so `data` is narrowed to
// this shape by hand before anything reads off it.
type TagLikeOption = { label: string; parent?: string | null };

// An option carrying a `parent` renders as a joined two-segment chip (parent|child) with the
// remove control lifted into the child segment. Tone is supplied by the caller rather than
// derived here, so this stays presentational. Parentless options fall through untouched.
export const MultiValueContainer = (
  props: MultiValueGenericProps & {
    parentVariant: BadgeVariant;
    childVariant: BadgeVariant;
    isIndeterminate?: boolean;
  },
) => {
  const { children, parentVariant, childVariant, isIndeterminate } = props;
  const data = props.data as TagLikeOption;

  if (!data.parent) {
    return <components.MultiValueContainer {...props} />;
  }

  const [, removeElement] = Children.toArray(children);

  return (
    <Badge
      className="max-w-full min-w-0 text-xs! wrap-anywhere"
      valueClassName={cn('max-w-full min-w-0 gap-x-2 text-xs! wrap-anywhere', isIndeterminate && 'text-black')}
      variant={parentVariant}
      valueVariant={childVariant}
      value={
        <>
          {data.label}
          {removeElement}
        </>
      }
    >
      {data.parent}
    </Badge>
  );
};
